/**
 * Profile Store
 * Manages user profile data and completion status
 * 
 * @author Varun Pandey
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Profile } from '@/api/types';

interface ProfileState {
  profile: Profile | null;
  completionPercentage: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setProfile: (profile: Profile | null) => void;
  updateProfile: (updates: Partial<Profile>) => void;
  setCompletionPercentage: (percentage: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearProfile: () => void;
}

export const useProfileStore = create<ProfileState>()(
  persist(
    (set) => ({
      profile: null,
      completionPercentage: 0,
      isLoading: false,
      error: null,

      setProfile: (profile) => {
        set({
          profile,
          completionPercentage: profile?.completionPercentage || 0,
        });
      },

      updateProfile: (updates) => {
        set((state) => ({
          profile: state.profile ? { ...state.profile, ...updates } : null,
        }));
      },

      setCompletionPercentage: (percentage) => {
        set({ completionPercentage: percentage });
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      clearProfile: () => {
        set({
          profile: null,
          completionPercentage: 0,
          error: null,
        });
      },
    }),
    {
      name: 'profile-storage',
    }
  )
);
